
import { useState, useEffect } from "react";
import { DerivativeCreation } from "@/data/models/types";
import { DerivativeData } from "@/types/derivatives";
import { DerivativeListItem } from "@/utils/supabase/types/derivatives";
import { fetchCreatorProfiles } from "@/utils/supabase/derivatives/fetchCommon";

// Update the hook to accept either the GraphQL-style data or the direct Supabase API format
export const useDerivativesFormatter = (derivativesData: DerivativeData[] | DerivativeListItem[] | null) => {
  const [formattedDerivatives, setFormattedDerivatives] = useState<DerivativeCreation[]>([]);
  
  useEffect(() => {
    console.log("useDerivativesFormatter - Initial data:", derivativesData);
    
    if (!derivativesData) {
      console.log("useDerivativesFormatter - No data provided");
      setFormattedDerivatives([]);
      return;
    }
    
    if (!Array.isArray(derivativesData) || derivativesData.length === 0) {
      console.log("useDerivativesFormatter - Empty array or not an array");
      setFormattedDerivatives([]);
      return;
    }
    
    // Filter out any undefined items before processing
    const validDerivativesData = derivativesData.filter(item => item !== undefined && item !== null);
    
    // Check if we're working with DerivativeListItem (Supabase API) format
    const isDirectApiFormat = validDerivativesData.length > 0 && 
      ('creatorId' in validDerivativesData[0] || 'thumbnailUrl' in validDerivativesData[0]);
    
    const creatorIds = [...new Set(validDerivativesData.map(item => {
      // Add null check before attempting to access properties
      if (!item) return null;
      
      if (isDirectApiFormat) {
        const typedItem = item as DerivativeListItem;
        return typedItem.creatorId || typedItem.userId || null;
      } else {
        const typedItem = item as DerivativeData;
        return typedItem.creator_id || typedItem.creatorId || typedItem.user_id || null;
      }
    }).filter(Boolean))];
    
    console.log("useDerivativesFormatter - Creator IDs:", creatorIds);
    
    const initialTransform = validDerivativesData.map((derivative): DerivativeCreation => {
      console.log("useDerivativesFormatter - Processing derivative:", derivative);
      
      // Ensure we have a valid derivative object
      if (!derivative) {
        console.warn("useDerivativesFormatter - Found undefined derivative item");
        // Return a placeholder derivative to avoid errors
        return {
          id: `placeholder-${Date.now()}-${Math.random()}`,
          creatorId: "",
          clipId: "",
          title: "Loading...",
          description: "",
          status: "pending",
          submittedAt: new Date().toISOString()
        };
      }
      
      // Handle both the GraphQL-style data and the direct API format
      if (isDirectApiFormat) {
        const item = derivative as DerivativeListItem;
        
        return {
          id: item.id,
          creatorId: item.creatorId || item.userId || "",
          creatorName: item.creatorName || "Créateur ReCut",
          creatorAvatar: item.creatorAvatar || '',
          clipId: item.clipId || "",
          title: item.title || "",
          description: item.description || '',
          thumbnail: item.thumbnailUrl || "",
          videoUrl: item.videoUrl || "",
          expressionType: item.expressionType || "edit",
          aspectRatio: item.aspectRatio || 16/9,
          status: (item.status as "approved" | "rejected" | "pending") || "pending",
          submittedAt: item.submittedAt,
          approvedAt: item.approvedAt,
          views: item.views,
          commentsCount: item.commentsCount,
          likesCount: item.likesCount,
          favoritesCount: item.favoritesCount
        };
      } else {
        // Original GraphQL/raw data format handling
        const item = derivative as DerivativeData;
        
        const creatorId = item.creator_id || item.creatorId || item.user_id || "";
        // Never default to "Original Creator" - use "Créateur ReCut" instead when no name is available
        const creatorName = item.creator_name || item.creatorName || item.creator || "Créateur ReCut";
        
        const views = typeof item.views === 'number' ? item.views : 0;
        const likesCount = item.likes_count || item.likesCount || 0;
        const favoritesCount = item.favorites_count || item.favoritesCount || 0;
        const commentsCount = item.comments_count || item.commentsCount || 0;
        
        console.log(`useDerivativesFormatter - Derivative ${item.id} metrics:`, {
          views,
          likesCount,
          favoritesCount,
          commentsCount
        });
        
        return {
          id: item.id,
          creatorId: creatorId as string,
          creatorName: creatorName === "Original Creator" ? "Créateur ReCut" : creatorName,
          creatorAvatar: item.creator_avatar || item.creatorAvatar || '',
          clipId: item.original_clip_id || item.clip_id || item.clipId || "",
          title: item.title || "",
          description: item.description || '',
          thumbnail: item.thumbnail || item.thumbnail_url || item.thumbnailUrl || "",
          videoUrl: item.video_url || item.videoUrl || "",
          expressionType: item.expression_type || item.expressionType || "edit",
          aspectRatio: item.aspect_ratio || item.aspectRatio || 16/9,
          status: (item.status as "approved" | "rejected" | "pending") || "pending",
          submittedAt: item.submitted_at || item.submittedAt || item.submitted_date || new Date().toISOString(),
          approvedAt: item.approved_at || item.approvedAt || item.approved_date,
          commentsCount: commentsCount,
          likesCount: likesCount,
          favoritesCount: favoritesCount,
          views: views
        };
      }
    });
    
    console.log("useDerivativesFormatter - Initial transform complete:", initialTransform);
    setFormattedDerivatives(initialTransform);
    
    if (creatorIds.length > 0) {
      console.log("useDerivativesFormatter - Fetching creator profiles");
      fetchCreatorProfiles(creatorIds as string[])
        .then(profileInfo => {
          console.log("useDerivativesFormatter - Profiles fetched:", profileInfo);
          const updatedTransform = initialTransform.map(derivative => {
            if (derivative.creatorId && profileInfo[derivative.creatorId]) {
              const profile = profileInfo[derivative.creatorId];
              return {
                ...derivative,
                creatorName: profile.name,
                creatorAvatar: profile.avatar
              };
            }
            // Make sure we never return "Original Creator" as a name
            if (derivative.creatorName === "Original Creator") {
              return {
                ...derivative,
                creatorName: "Créateur ReCut"
              };
            }
            return derivative;
          });
          
          console.log("useDerivativesFormatter - Final transform with profiles:", updatedTransform);
          setFormattedDerivatives(updatedTransform);
        })
        .catch(err => {
          console.error('useDerivativesFormatter - Error fetching creator profiles:', err);
        });
    }
  }, [derivativesData]);
  
  return formattedDerivatives;
};
